import React, { useState, useEffect } from 'react';
import { Typography, Button } from "@material-tailwind/react";
import config from '../config';
import IconSelector from './IconSelector';

export function HelpCenter({ companyId }) {
    const [categories, setCategories] = useState([]);
    const [articles, setArticles] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedArticle, setSelectedArticle] = useState(null);

    useEffect(() => {
        fetchCategories();
        fetchArticles();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await fetch(`${config.apiUrl}/get-faq-categories?company_id=${companyId}`);
            if (response.ok) {
                const data = await response.json();
                setCategories(data);
            }
        } catch (error) {
            console.error('Fout bij ophalen categorieën:', error);
        }
    };

    const fetchArticles = async () => {
        try {
            const response = await fetch(`${config.apiUrl}/get-faq-articles?company_id=${companyId}`);
            if (response.ok) {
                const data = await response.json();
                setArticles(data);
            }
        } catch (error) {
            console.error('Fout bij ophalen artikelen:', error);
        }
    };

    const handleBack = () => {
        if (selectedArticle) {
            setSelectedArticle(null);
        } else if (selectedCategory) {
            setSelectedCategory(null);
        }
    };

    const getArticleCountForCategory = (categoryId) => {
        return articles.filter(article => article.category_id === categoryId).length;
    };

    const getCategoryArticles = (categoryId) => {
        return articles.filter(article =>
            article.category_id === categoryId && !article.subcategory_id
        );
    };

    return (
        <div className="flex flex-col h-full">
            <div className="border-b p-4">
                <Typography variant="h5" className="dark:text-white">
                    Help Centrum
                </Typography>
            </div>

            <div className="flex-1 overflow-y-auto">
                {selectedArticle ? (
                    <div className="px-4">
                        <Button
                            variant='text'
                            size="sm"
                            onClick={handleBack}
                            className="mb-4 mt-1 flex items-center gap-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 capitalize"
                        >
                            ← Terug naar overzicht
                        </Button>
                        <h3 className="text-lg font-semibold mb-4 dark:text-white">
                            {selectedArticle.title}
                        </h3>
                        <div
                            className="prose max-w-none text-sm pb-12 flex flex-col gap-2 dark:text-gray-300 [&_p]:dark:text-gray-300 [&_ul]:dark:text-gray-300 [&_ol]:dark:text-gray-300"
                            dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
                        />
                    </div>
                ) : selectedCategory ? (
                    <div className="px-4">
                        <Button
                            variant='text'
                            size="sm"
                            onClick={handleBack}
                            className="mb-4 mt-1 flex items-center gap-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 capitalize"
                        >
                            ← Terug naar categorieën
                        </Button>
                        <div className="flex items-center gap-4 mb-4 pb-2 border-b">
                            <div className="w-10 h-10 flex items-center justify-center">
                                <IconSelector selectedIcon={selectedCategory.icon} isEditing={false} onSelect={() => { }} />
                            </div>
                            <Typography variant="h4" className="dark:text-white text-lg">
                                {selectedCategory.name}
                            </Typography>
                        </div>

                        {/* Subcategories with their articles - Always at the top */}
                        {selectedCategory.subcategories.length > 0 && (
                            <div className="py-2 border-b">
                                {selectedCategory.subcategories.map((subcategory) => (
                                    <div key={subcategory.subcategory_id} className="space-y-3">
                                        <Typography variant="h6" className="text-md">
                                            {subcategory.name}
                                        </Typography>
                                        <div className="space-y-2">
                                            {articles
                                                .filter(article => article.subcategory_id === subcategory.subcategory_id)
                                                .map((article) => (
                                                    <div
                                                        key={article.article_id}
                                                        onClick={() => setSelectedArticle(article)}
                                                        className="p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                                                            </svg>
                                                            <Typography variant="small" className="font-medium dark:text-white">
                                                                {article.title}
                                                            </Typography>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Articles without subcategory - Always at the bottom */}
                        {getCategoryArticles(selectedCategory.category_id).length > 0 && (
                            <div className="py-2">
                                <Typography variant="h6" className="text-md mb-2">
                                    Overige artikelen
                                </Typography>
                                <div className="space-y-2">
                                    {getCategoryArticles(selectedCategory.category_id).map((article) => (
                                        <div
                                            key={article.article_id}
                                            onClick={() => setSelectedArticle(article)}
                                            className="p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                                        >
                                            <div className="flex items-center gap-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                                                </svg>
                                                <Typography variant="small" className="font-medium dark:text-white">
                                                    {article.title}
                                                </Typography>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                        {categories.map((category) => (
                            <div
                                key={category.category_id}
                                onClick={() => setSelectedCategory(category)}
                                className="p-2 rounded-lg border dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 flex gap-2 items-center"
                            >
                                <IconSelector selectedIcon={category.icon} isEditing={false} onSelect={() => { }} />
                                <div>
                                    <Typography variant="h6" className="text-sm font-medium dark:text-white">
                                        {category.name}
                                    </Typography>
                                    <Typography variant="small" className="text-gray-500 dark:text-gray-400">
                                        {getArticleCountForCategory(category.category_id)} artikel{getArticleCountForCategory(category.category_id) !== 1 ? 'en' : ''}
                                    </Typography>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default HelpCenter;